import { getUsers, putUserRole } from '@api/userAPI';
import { ListWrapper } from '@components/common';
import VPagination from '@components/Pagination/VPagination';
import { useToast } from '@hooks/useToast';
import qs from 'qs';
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import VUserList from './VUserList';
import VUserToolbar from './VUserToolbar';

const ROLES = ['USER', 'MANAGER', 'ADMIN'];

const stringOf = (value: unknown): string => (typeof value === 'string' ? value : '');

const UserList = (): JSX.Element => {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const query = useMemo(() => qs.parse(search.replace('?', '')), [search]);
  const page = stringOf(query.page);
  const userRole = stringOf(query.userRole);
  const keyword = stringOf(query.username);

  const [users, setUsers] = useState<Array<User>>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [number, setNumber] = useState(0);
  const [searchInput, setSearchInput] = useState(keyword);

  const fetchUsers = async () => {
    const response = await getUsers({ page, userRole, username: keyword });
    if (response?.data?.content) {
      setUsers(
        response.data.content.map((el: any) => ({
          username: el.username,
          userRole: el.userRole,
        })),
      );
      setTotalPages(response?.data?.totalPages);
      setNumber(
        response?.data?.number > response?.data?.totalPages - 1
          ? Math.max(response?.data?.totalPages - 1, 0)
          : response?.data?.number < 0
          ? 0
          : response?.data?.number,
      );
    }
  };

  useEffect(() => {
    fetchUsers();
    setSearchInput(keyword);
    window.scrollTo(0, 0);
  }, [search]);

  /** 필터가 바뀌면 조회 중인 페이지를 첫 페이지로 되돌립니다. */
  const updateQuery = (next: { userRole?: string; username?: string }) => {
    const nextRole = next.userRole !== undefined ? next.userRole : userRole;
    const nextKeyword = next.username !== undefined ? next.username : keyword;
    const queryParams: { [key: string]: string | number } = { page: 0 };
    if (nextRole) queryParams.userRole = nextRole;
    if (nextKeyword) queryParams.username = nextKeyword;
    navigate({ pathname, search: qs.stringify(queryParams) });
  };

  const changeUserRole = async (username: string, role: string) => {
    const response = await putUserRole(username, role);
    if (response.status === 204) {
      toast({ message: '역할이 변경되었습니다.', type: 'success' });
      await fetchUsers();
      return;
    }
    if (response.status >= 400) {
      toast({ message: `${response.data.message}`, type: 'warning' });
    }
  };

  const vUserToolbarProps = {
    roles: ROLES,
    currentRole: userRole,
    searchInput,
    keyword,
    handleTabClick: (role: string) => {
      if (role === userRole) return;
      updateQuery({ userRole: role });
    },
    handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => {
      setSearchInput(event.target.value);
    },
    handleSearchSubmit: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      updateQuery({ username: searchInput.trim() });
    },
    handleSearchReset: () => {
      setSearchInput('');
      updateQuery({ username: '' });
    },
  };

  const vUserListProps = {
    users,
    roles: ROLES,
    each: (user: User) => ({
      ...user,
      handleChange: async (event: ChangeEvent<HTMLSelectElement>) => {
        await changeUserRole(user.username, event.target.value);
      },
    }),
  };

  const vPaginationProps = {
    totalPages,
    number,
  };

  return (
    <ListWrapper>
      <div>
        <VUserToolbar {...vUserToolbarProps} />
        <VUserList {...vUserListProps} />
      </div>
      <VPagination {...vPaginationProps} />
    </ListWrapper>
  );
};

export default UserList;
